import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

// How a device code nobody has mapped yet is linked to a member or staff user. The backend applies it only on
// Pull All Users and on the code's first punch (services/biometric/matching.py), and links a code only when exactly
// one person matches. Everything else uses the saved mapping.

export const DEFAULT_SUFFIX_DIGITS = 5;
const SUFFIX_DIGIT_CHOICES = [3, 4, 5, 6, 7, 8, 9];

const METHODS = {
  NONE: { option: "None (map by hand only)", badge: "Manual mapping" },
  MEMBER_NUMBER: { option: "Member number (code 125 = member #125)", badge: "Member #" },
  PHONE: { option: "Full phone number (+91 ignored)", badge: "Phone number" },
  PHONE_SUFFIX: { option: "Last digits of phone number", badge: null },
};

function methodOf(device) {
  return METHODS[device?.matchMethod] ? device.matchMethod : "NONE";
}

export function matchMethodLabel(device) {
  const method = methodOf(device);
  if (method === "PHONE_SUFFIX") return `Last ${device.phoneSuffixDigits || DEFAULT_SUFFIX_DIGITS} phone digits`;
  return METHODS[method].badge;
}

export function MatchMethodBadge({ device }) {
  const colors =
    methodOf(device) === "NONE"
      ? "text-muted-foreground border-border/60"
      : "border-sky-500/30 text-sky-400 bg-sky-500/10";
  return (
    <Badge variant="outline" className={`text-[10px] font-mono whitespace-nowrap ${colors}`}>
      {matchMethodLabel(device)}
    </Badge>
  );
}

export function MatchMethodFields({ form, setForm }) {
  const method = methodOf(form);
  return (
    <>
      <div>
        <Label className="text-xs">Link Device Codes By</Label>
        <Select value={method} onValueChange={(v) => setForm((p) => ({ ...p, matchMethod: v }))}>
          <SelectTrigger className="rounded-sm h-8 text-xs mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(METHODS).map(([value, { option }]) => (
              <SelectItem key={value} value={value}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {method === "PHONE_SUFFIX" && (
        <div>
          <Label className="text-xs">Number of Phone Digits</Label>
          <Select
            value={String(form.phoneSuffixDigits || DEFAULT_SUFFIX_DIGITS)}
            onValueChange={(v) => setForm((p) => ({ ...p, phoneSuffixDigits: Number(v) }))}
          >
            <SelectTrigger className="rounded-sm h-8 text-xs mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUFFIX_DIGIT_CHOICES.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  Last {n} digits
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="sm:col-span-2">
        <p className="text-[11px] text-muted-foreground bg-secondary/30 p-2 rounded-sm border border-border/50">
          <strong>Note:</strong> Applied on Pull All Users and on a code&apos;s first punch. A code is linked only when
          exactly one member or staff user matches; otherwise map it by hand. Mapped codes are never changed. Choose
          None when the device uses its own employee codes.
        </p>
      </div>
    </>
  );
}
